import { useState, useCallback, useRef } from 'react';
import { api } from '@/utils/api';
import { uploadToR2 } from '@/utils/uploadUtils';
import Uppy from '@uppy/core';
import Tus from '@uppy/tus';
import env from '@/utils/loadEnv';
import { useUserProfile } from '@/context/UserProfileContext';
import { MediaItem } from './usePostForm';

export function useMediaUpload() {
  const { profile: userProfile } = useUserProfile();
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [mediaDimensions, setMediaDimensions] = useState<{ [key: number]: { width: number; height: number } }>({});

  // Create Uppy instance for video uploads
  const createUppy = useCallback((setMediaCallback: typeof setMedia) => {
    return new Uppy({
      restrictions: { maxFileSize: 200 * 1024 * 1024 },
      debug: true,
      autoProceed: true
    })
      .use(Tus, {
        endpoint: `${env.VITE_PUBLIC_API_URL}/posts/get-upload-url`,
        chunkSize: 150 * 1024 * 1024,
      })
      .on('file-added', (file) => {
        try {
          const videoPreviewUrl = URL.createObjectURL(file.data);
          setMediaCallback((prev: MediaItem[]) => [
            ...prev,
            {
              id: file.id,
              type: 'video',
              file: file.data,
              url: videoPreviewUrl,
              uploading: true
            },
          ]);
        } catch (error) {
          console.error('Error creating video preview URL:', error);
        }
      })
      .on('upload-progress', (file, progress) => {
        console.log(file?.id, progress.bytesUploaded, progress.bytesTotal);
      })
      .on('upload-success', (file, response) => {
        console.log('Upload success:', file, response);
        const uploadURL = response?.uploadURL || '';
        const uid = uploadURL.split('/').pop() || file?.id || 'unknown';

        setMediaCallback((prev: MediaItem[]) =>
          prev.map((item) =>
            item.id === file?.id
              ? {
                ...item,
                id: uid,
                uploading: false,
              }
              : item
          )
        );
      });
  }, []);

  // Use any for now to avoid complex type issues with Uppy
  const uppyRef = useRef<any>(createUppy(setMedia));

  // Handle image load to calculate dimensions
  const handleImageLoad = useCallback((index: number, event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    setMediaDimensions((prev) => ({
      ...prev,
      [index]: { width: img.naturalWidth, height: img.naturalHeight },
    }));
  }, []);

  // Handle image selection
  const handleImageSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files || files.length === 0) {
      return;
    }

    try {
      // Fetch presigned URLs for all files
      const { data: presignedUrls } = await api.get('/posts/presigned-url', {
        headers: {
          'url-count': files.length,
        },
      });

      // Create new media items
      const newMedia = presignedUrls.map((presignedUrl: { id: string; url: string }, index: number) => ({
        id: presignedUrl.id,
        type: 'image' as const,
        url: URL.createObjectURL(files[index]!),
        uploading: true,
        file: files[index]
      }));

      setMedia(prev => [...prev, ...newMedia]);

      // Upload files
      await Promise.all(
        newMedia.map(async (item: MediaItem, index: number) => {
          try {
            await uploadToR2(presignedUrls[index].url, files[index]);
            setMedia(prev =>
              prev.map(mediaItem =>
                mediaItem.id === item.id
                  ? { ...mediaItem, uploading: false }
                  : mediaItem,
              ),
            );
          } catch (uploadError) {
            console.error(`Error uploading image ${item.id}:`, uploadError);
            setMedia(prev =>
              prev.map(mediaItem =>
                mediaItem.id === item.id
                  ? { ...mediaItem, uploading: false, error: true }
                  : mediaItem,
              ),
            );
          }
        }),
      );
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      event.target.value = '';
    }
  }, []);

  // Handle video selection
  const handleVideoSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files || files.length === 0 || !userProfile) {
      return;
    }

    try {
      console.log('Adding video file:', files[0]);
      uppyRef.current.addFile({
        source: 'local',
        name: `${userProfile.id}_${Date.now()}`,
        type: files[0].type,
        data: files[0]
      });
    } catch (error) {
      console.error('Error uploading videos:', error);
    } finally {
      event.target.value = '';
    }
  }, [userProfile]);

  // Handle media deletion
  const handleDeleteMedia = useCallback(async (index: number) => {
    const item = media[index];
    try {
      if (item.type === 'image') {
        await api.delete(`/posts/image/${item.id}`);
      } else if (item.type === 'video') {
        await api.delete(`/posts/video/${item.id}`);
      }
      setMedia(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error deleting media:', error);
    }
  }, [media]);

  // Add media programmatically (used by shared media)
  const addMedia = useCallback((newMedia: MediaItem) => {
    setMedia(prev => [...prev, newMedia]);
  }, []);

  // Reset media state
  const resetMedia = useCallback(() => {
    setMedia([]);
    setMediaDimensions({});
  }, []);

  return {
    media,
    mediaDimensions,
    uppy: uppyRef.current,
    handleImageLoad,
    handleImageSelect,
    handleVideoSelect,
    handleDeleteMedia,
    addMedia,
    resetMedia,
    setMedia
  };
}
